/**
 * SubcontractorStatusCard V2
 * Displays a single subcontractor's full status on an assignment
 * Including: Work status, Invoice, Report details inline, Attestation, SMS
 */

import { useState } from 'react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import {
  Phone,
  Mail,
  Check,
  X,
  AlertCircle,
  FileText,
  Clock,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Image,
  Leaf,
  Receipt
} from 'lucide-react'
import type { TgWorkStatus, TgInvoiceStatus } from '../../types/statusTypes'
import { SubcontractorSMSSection } from './SubcontractorSMSSection'
import {
  getTgWorkStatusBadgeClasses,
  getTgWorkStatusName,
  getTgInvoiceStatusBadgeClasses,
  getTgInvoiceStatusName,
  canRegisterTgInvoice
} from '../../services/statusSystemV2'

interface Subcontractor {
  id: string
  name: string
  email?: string
  phone?: string
  color?: string
}

interface ArticleImage {
  url: string
  name: string
  uploaded_at: string
  type: 'before' | 'after' | 'other'
}

interface ReportArticle {
  article_id: string | null  // null för utlägg
  article_name: string
  unit: string
  planned_quantity: number
  reported_quantity: number
  notes?: string
  images?: ArticleImage[]
  // Utlägg-fält
  is_expense?: boolean
  expense_amount_incl_vat?: number | null
  expense_amount_excl_vat?: number | null
  expense_vat_rate?: number | null
}

interface ReportImage {
  url: string
  type: 'before' | 'after' | 'other'
  caption?: string
}

interface ReportData {
  id: string
  report_text?: string
  reported_at?: string
  admin_feedback?: string
  feedback_at?: string
  reported_articles?: ReportArticle[]
  report_images?: ReportImage[]
  is_draft?: boolean
  attested_at?: string
  attested_by?: string
  draft_saved_at?: string
  draft_saved_by?: string
}

interface SubcontractorStatusCardProps {
  subcontractor: Subcontractor
  assignmentId: string

  // Status
  workStatus: TgWorkStatus
  invoiceStatus: TgInvoiceStatus

  // Full report data (optional - for inline display)
  report?: ReportData | null

  // Timestamps (fallback if report not provided)
  declinedAt?: string | null
  declinedReason?: string | null
  reportedAt?: string | null
  attestedAt?: string | null
  invoiceReceivedAt?: string | null
  invoiceNumber?: string | null

  // Report info (fallback if report not provided)
  hasReport?: boolean
  isDraft?: boolean
  hasFeedback?: boolean

  // Admin reporting form (render prop from parent)
  isReportingFor?: boolean
  reportFormContent?: React.ReactNode
  onStartReporting?: () => void
  onCancelReporting?: () => void

  // Callbacks
  onRemove: () => void
  onViewReport?: () => void
  onWorkStatusChange?: (status: TgWorkStatus) => void
  onInvoiceStatusChange?: (status: TgInvoiceStatus) => void
  onNavigateToSubcontractor: () => void
  onAttest?: () => void
  onRemoveAttest?: () => void
  onRequestComplement?: () => void
  onReportFor?: () => void
  onRegisterInvoice?: () => void

  // State
  disabled?: boolean
  isLocked?: boolean
  showReportSection?: boolean
  showSMSSection?: boolean

  // Collapsible
  defaultExpanded?: boolean
}

export function SubcontractorStatusCard({
  subcontractor,
  assignmentId,
  workStatus,
  invoiceStatus,
  report,
  declinedAt,
  declinedReason,
  reportedAt,
  attestedAt,
  invoiceReceivedAt,
  invoiceNumber,
  hasReport,
  isDraft,
  hasFeedback,
  isReportingFor = false,
  reportFormContent,
  onStartReporting,
  onCancelReporting,
  onRemove,
  onViewReport: _onViewReport,
  onWorkStatusChange: _onWorkStatusChange,
  onInvoiceStatusChange: _onInvoiceStatusChange,
  onNavigateToSubcontractor,
  onAttest,
  onRemoveAttest,
  onRequestComplement,
  onReportFor,
  onRegisterInvoice,
  disabled = false,
  isLocked = false,
  showReportSection = true,
  showSMSSection = true,
  defaultExpanded = false
}: SubcontractorStatusCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const canChangeInvoice = canRegisterTgInvoice(workStatus)

  // Use report data if available, otherwise fall back to props
  const effectiveReportedAt = report?.reported_at || reportedAt
  const effectiveAttestedAt = report?.attested_at || attestedAt
  const effectiveIsDraft = report?.is_draft ?? isDraft
  const effectiveHasReport = report ? (!!report.reported_at || report.is_draft) : hasReport
  const effectiveHasFeedback = report ? !!report.admin_feedback : hasFeedback
  const reportArticles = report?.reported_articles || []

  // Collect all images: from report_images AND from each article's images
  const generalImages = report?.report_images || []
  const articleImages = reportArticles.flatMap(article =>
    (article.images || []).map(img => ({
      url: img.url,
      type: img.type as 'before' | 'after' | 'other',
      caption: img.name,
      articleName: article.article_name
    }))
  )

  // Combine all images (include all types now)
  const allImages = [
    ...generalImages.map(img => ({ ...img, articleName: undefined as string | undefined })),
    ...articleImages
  ]

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return null
    try {
      return format(new Date(timestamp), 'yyyy-MM-dd HH:mm', { locale: sv })
    } catch {
      return null
    }
  }

  const formatDate = (timestamp: string | null | undefined) => {
    if (!timestamp) return null
    try {
      return format(new Date(timestamp), 'yyyy-MM-dd', { locale: sv })
    } catch {
      return null
    }
  }

  const getStatusIcon = () => {
    switch (workStatus) {
      case 'godkand':
        return <Check className="h-4 w-4" />
      case 'avsagt':
        return <X className="h-4 w-4" />
      case 'komplettering':
        return <AlertCircle className="h-4 w-4" />
      case 'rapporterad':
        return <FileText className="h-4 w-4" />
      case 'ej_rapporterad':
        return <Clock className="h-4 w-4" />
      default:
        return <Leaf className="h-4 w-4" />
    }
  }

  const beforeImages = allImages.filter(img => img.type === 'before')
  const afterImages = allImages.filter(img => img.type === 'after')
  const otherImages = allImages.filter(img => img.type === 'other')

  return (
    <div className={`border border-nature-200 dark:border-nature-700 rounded-lg overflow-hidden ${
      workStatus === 'avsagt' ? 'opacity-60' : ''
    }`}>
      {/* Header with name - clickable to expand/collapse */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsExpanded(!isExpanded); } }}
        className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-nature-50 dark:bg-nature-700/50 hover:bg-nature-100 dark:hover:bg-nature-600/50 transition-colors cursor-pointer"
      >
        {/* Mobile: stacked layout */}
        <div className="sm:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="text-nature-400 flex-shrink-0">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </div>
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: subcontractor.color || '#6B7280' }}
              />
              <span className="font-semibold text-nature-900 dark:text-nature-100 uppercase truncate">
                {subcontractor.name}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onNavigateToSubcontractor()
                }}
                className="p-1.5 text-primary-500 hover:text-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/20 rounded"
                title="Visa trädgårdsmästare"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
              {!isLocked && !disabled && workStatus !== 'godkand' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove()
                  }}
                  className={`p-1.5 rounded ${workStatus === 'avsagt' ? 'text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20' : 'text-nature-400 hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20'}`}
                  title={workStatus === 'avsagt' ? 'Bekräfta avsägning och ta bort' : 'Ta bort trädgårdsmästare'}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          {/* Status badge below name on mobile */}
          {!isExpanded && (
            <div className="mt-1.5 ml-9">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getTgWorkStatusBadgeClasses(workStatus)}`}>
                {getStatusIcon()}
                {getTgWorkStatusName(workStatus)}
              </span>
            </div>
          )}
        </div>

        {/* Desktop: horizontal layout */}
        <div className="hidden sm:flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-nature-400">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </div>
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: subcontractor.color || '#6B7280' }}
            />
            <span className="font-semibold text-nature-900 dark:text-nature-100 uppercase">
              {subcontractor.name}
            </span>

            {/* Status badge in header when collapsed */}
            {!isExpanded && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getTgWorkStatusBadgeClasses(workStatus)}`}>
                {getStatusIcon()}
                {getTgWorkStatusName(workStatus)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onNavigateToSubcontractor()
              }}
              className="p-1 text-primary-500 hover:text-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/20 rounded"
              title="Visa trädgårdsmästare"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
            {!isLocked && !disabled && workStatus !== 'avsagt' && workStatus !== 'godkand' && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove()
                }}
                className="p-1 text-nature-400 hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                title="Ta bort trädgårdsmästare"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content - collapsible */}
      {isExpanded && (
      <div className="p-4 space-y-4">
        {/* Two-column status layout: ARBETE | FAKTURA */}
        <div className="grid grid-cols-2 gap-4">
          {/* ARBETE (Work Status) */}
          <div>
            <h4 className="text-xs font-medium text-nature-500 dark:text-nature-400 uppercase mb-2">Arbete</h4>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${getTgWorkStatusBadgeClasses(workStatus)}`}>
              {getStatusIcon()}
              {getTgWorkStatusName(workStatus)}
            </span>
          </div>

          {/* FAKTURA (Invoice Status) */}
          <div>
            <h4 className="text-xs font-medium text-nature-500 dark:text-nature-400 uppercase mb-2">Faktura</h4>
            <div className="space-y-1">
              {invoiceStatus === 'ej_fakturerad' && canChangeInvoice && !isLocked && !disabled && onRegisterInvoice ? (
                <button
                  type="button"
                  onClick={onRegisterInvoice}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${getTgInvoiceStatusBadgeClasses(invoiceStatus)} hover:ring-2 hover:ring-primary-300 dark:hover:ring-primary-600 cursor-pointer transition-all`}
                >
                  <Clock className="h-4 w-4" />
                  {getTgInvoiceStatusName(invoiceStatus)}
                </button>
              ) : (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${getTgInvoiceStatusBadgeClasses(invoiceStatus)}`}>
                  {invoiceStatus === 'faktura_mottagen' ? <Receipt className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                  {getTgInvoiceStatusName(invoiceStatus)}
                </span>
              )}

              {invoiceStatus === 'faktura_mottagen' && invoiceNumber && (
                <p className="text-xs text-nature-500 dark:text-nature-400">
                  {invoiceNumber} · {formatDate(invoiceReceivedAt)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Declined info */}
        {workStatus === 'avsagt' && declinedAt && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm border border-red-200 dark:border-red-800">
            <p className="text-red-700 dark:text-red-300 font-medium">
              Avsagt sig: {formatTimestamp(declinedAt)}
            </p>
            {declinedReason && (
              <p className="text-red-600 dark:text-red-400 italic mt-1">"{declinedReason}"</p>
            )}
          </div>
        )}

        {/* Report section (inline) */}
        {showReportSection && effectiveHasReport && !effectiveIsDraft && (
          <>
            <hr className="border-nature-200 dark:border-nature-700" />

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Rapport</h4>
                {effectiveReportedAt && (
                  <span className="text-xs text-nature-500 dark:text-nature-400">
                    Inskickad: {formatDate(effectiveReportedAt)}
                  </span>
                )}
              </div>

              {/* Articles table */}
              {reportArticles.filter(a => !a.is_expense).length > 0 && (
                <div className="bg-nature-50 dark:bg-nature-700/50 rounded-lg overflow-hidden mb-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-nature-200 dark:border-nature-600">
                        <th className="text-left px-3 py-2 text-xs font-medium text-nature-500 dark:text-nature-400">Artikel</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-nature-500 dark:text-nature-400">Planerat</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-nature-500 dark:text-nature-400">Utfört</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-nature-500 dark:text-nature-400">Anteckning</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportArticles.filter(a => !a.is_expense).map((article, idx) => (
                        <tr key={idx} className="border-b border-nature-200 dark:border-nature-600 last:border-0">
                          <td className="px-3 py-2 text-nature-900 dark:text-nature-100">{article.article_name}</td>
                          <td className="px-3 py-2 text-right text-nature-600 dark:text-nature-400">
                            {article.planned_quantity} {article.unit}
                          </td>
                          <td className="px-3 py-2 text-right text-nature-900 dark:text-nature-100 font-medium">
                            {article.reported_quantity} {article.unit}
                          </td>
                          <td className="px-3 py-2 text-nature-500 dark:text-nature-400 text-xs">
                            {article.notes || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Expenses table */}
              {reportArticles.filter(a => a.is_expense).length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg overflow-hidden mb-3">
                  <div className="px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-300 border-b border-amber-200 dark:border-amber-700">
                    Utlägg & Kvitton
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-amber-200 dark:border-amber-700">
                        <th className="text-left px-3 py-2 text-xs font-medium text-amber-600 dark:text-amber-400">Beskrivning</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-amber-600 dark:text-amber-400">Belopp inkl moms</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-amber-600 dark:text-amber-400">Belopp ex moms</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-amber-600 dark:text-amber-400">Moms</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportArticles.filter(a => a.is_expense).map((expense, idx) => (
                        <tr key={idx} className="border-b border-amber-200 dark:border-amber-700 last:border-0">
                          <td className="px-3 py-2 text-nature-900 dark:text-nature-100">{expense.article_name}</td>
                          <td className="px-3 py-2 text-right text-nature-900 dark:text-nature-100 font-medium">
                            {expense.expense_amount_incl_vat?.toFixed(2)} kr
                          </td>
                          <td className="px-3 py-2 text-right text-nature-600 dark:text-nature-400">
                            {expense.expense_amount_excl_vat?.toFixed(2)} kr
                          </td>
                          <td className="px-3 py-2 text-right text-nature-500 dark:text-nature-400 text-xs">
                            {expense.expense_vat_rate}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Images */}
              {allImages.length > 0 && (
                <div className="mb-3 space-y-3">
                  {beforeImages.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-xs text-nature-500 dark:text-nature-400 mb-1">
                        <Image className="h-3 w-3" />
                        <span>Före ({beforeImages.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {beforeImages.map((img, idx) => (
                          <div key={idx} className="flex flex-col items-center">
                            <a
                              href={img.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-16 h-16 rounded-lg overflow-hidden border border-nature-200 dark:border-nature-600 hover:border-primary-500 transition-colors"
                            >
                              <img
                                src={img.url}
                                alt={img.caption || `Före-bild ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </a>
                            {img.articleName && (
                              <span className="text-[10px] text-nature-400 dark:text-nature-500 mt-1 max-w-[100px] leading-tight text-center line-clamp-2" title={img.articleName}>
                                {img.articleName}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {afterImages.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-xs text-nature-500 dark:text-nature-400 mb-1">
                        <Image className="h-3 w-3" />
                        <span>Efter ({afterImages.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {afterImages.map((img, idx) => (
                          <div key={idx} className="flex flex-col items-center">
                            <a
                              href={img.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-16 h-16 rounded-lg overflow-hidden border border-nature-200 dark:border-nature-600 hover:border-primary-500 transition-colors"
                            >
                              <img
                                src={img.url}
                                alt={img.caption || `Efter-bild ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </a>
                            {img.articleName && (
                              <span className="text-[10px] text-nature-400 dark:text-nature-500 mt-1 max-w-[100px] leading-tight text-center line-clamp-2" title={img.articleName}>
                                {img.articleName}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {otherImages.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-xs text-nature-500 dark:text-nature-400 mb-1">
                        <Image className="h-3 w-3" />
                        <span>Övrigt ({otherImages.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {otherImages.map((img, idx) => (
                          <div key={idx} className="flex flex-col items-center">
                            <a
                              href={img.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-16 h-16 rounded-lg overflow-hidden border border-nature-200 dark:border-nature-600 hover:border-primary-500 transition-colors"
                            >
                              <img
                                src={img.url}
                                alt={img.caption || `Bild ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </a>
                            {img.articleName && (
                              <span className="text-[10px] text-nature-400 dark:text-nature-500 mt-1 max-w-[100px] leading-tight text-center line-clamp-2" title={img.articleName}>
                                {img.articleName}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Report notes */}
              {report?.report_text && (
                <div className="text-sm text-nature-700 dark:text-nature-300 italic">
                  Anteckning: "{report.report_text}"
                </div>
              )}
            </div>
          </>
        )}

        {/* Draft indicator */}
        {showReportSection && effectiveIsDraft && !isReportingFor && (
          <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm text-yellow-700 dark:text-yellow-300">
            Utkast sparad - ej inskickad
            {report?.draft_saved_at && (
              <span className="text-yellow-600 dark:text-yellow-400">
                {' '}({format(new Date(report.draft_saved_at), 'yyyy-MM-dd HH:mm', { locale: sv })}
                {report.draft_saved_by && ` av ${report.draft_saved_by}`})
              </span>
            )}
          </div>
        )}

        {/* Admin reporting form (when editing) */}
        {showReportSection && isReportingFor && reportFormContent && (
          <>
            <hr className="border-nature-200 dark:border-nature-700" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-nature-900 dark:text-nature-100">
                  Rapportera åt {subcontractor.name}
                </h4>
                {onCancelReporting && (
                  <button
                    type="button"
                    onClick={onCancelReporting}
                    className="text-xs text-nature-500 hover:text-nature-700"
                  >
                    Avbryt
                  </button>
                )}
              </div>
              {reportFormContent}
            </div>
          </>
        )}

        {/* "Start reporting" button for non-reported TGs (also shows for drafts) */}
        {showReportSection && (!effectiveHasReport || effectiveIsDraft) && !isReportingFor && onStartReporting && !isLocked && !disabled && (
          <>
            <hr className="border-nature-200 dark:border-nature-700" />
            <button
              type="button"
              onClick={onStartReporting}
              className="w-full py-2 text-sm text-left text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors px-2"
            >
              {effectiveIsDraft ? 'Fortsätt rapportera' : 'Rapportera'} åt {subcontractor.name}
            </button>
          </>
        )}

        {/* Attestation section */}
        {showReportSection && effectiveHasReport && !effectiveIsDraft && (
          <>
            <hr className="border-nature-200 dark:border-nature-700" />

            <div>
              {effectiveAttestedAt ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <Check className="h-4 w-4" />
                    <span>Attesterad {formatDate(effectiveAttestedAt)}</span>
                    {report?.attested_by && <span>av {report.attested_by}</span>}
                  </div>

                  {!isLocked && !disabled && (
                    <div className="flex items-center gap-2">
                      {onRemoveAttest && (
                        <button
                          type="button"
                          onClick={onRemoveAttest}
                          className="text-xs text-nature-500 hover:text-red-500"
                        >
                          Ta bort attestering
                        </button>
                      )}
                      {onRequestComplement && (
                        <button
                          type="button"
                          onClick={onRequestComplement}
                          className="text-xs text-orange-600 hover:text-orange-700"
                        >
                          Begär komplettering
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {onAttest && (
                    <button
                      type="button"
                      onClick={onAttest}
                      disabled={isLocked || disabled}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg disabled:opacity-50"
                    >
                      Attestera
                    </button>
                  )}
                  {onRequestComplement && (
                    <button
                      type="button"
                      onClick={onRequestComplement}
                      disabled={isLocked || disabled}
                      className="px-3 py-1.5 border border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-sm rounded-lg disabled:opacity-50"
                    >
                      Begär komplettering
                    </button>
                  )}
                  {onReportFor && (
                    <button
                      type="button"
                      onClick={onReportFor}
                      disabled={isLocked || disabled}
                      className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    >
                      Rapportera åt {subcontractor.name.split(' ')[0]}
                    </button>
                  )}
                </div>
              )}

              {/* Admin feedback (komplettering) */}
              {effectiveHasFeedback && report?.admin_feedback && (
                <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-sm">
                  <p className="text-orange-700 dark:text-orange-300 font-medium">Komplettering begärd:</p>
                  <p className="text-orange-600 dark:text-orange-400 italic">"{report.admin_feedback}"</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Contact info (compact) */}
        {(subcontractor.phone || subcontractor.email) && (
          <>
            <hr className="border-nature-200 dark:border-nature-700" />
            <div className="flex flex-wrap gap-4 text-sm text-nature-500 dark:text-nature-400">
              {subcontractor.phone && (
                <a
                  href={`tel:${subcontractor.phone}`}
                  className="flex items-center gap-1 hover:text-nature-700 dark:hover:text-nature-300"
                >
                  <Phone className="h-3.5 w-3.5" />
                  {subcontractor.phone}
                </a>
              )}
              {subcontractor.email && (
                <a
                  href={`mailto:${subcontractor.email}`}
                  className="flex items-center gap-1 hover:text-nature-700 dark:hover:text-nature-300"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {subcontractor.email}
                </a>
              )}
            </div>
          </>
        )}

        {/* SMS Section */}
        {showSMSSection && subcontractor.phone && assignmentId && (
          <>
            <hr className="border-nature-200 dark:border-nature-700" />
            <SubcontractorSMSSection
              assignmentId={assignmentId}
              subcontractorId={subcontractor.id}
              subcontractorName={subcontractor.name}
              subcontractorPhone={subcontractor.phone}
              compact={false}
            />
          </>
        )}
      </div>
      )}
    </div>
  )
}
